import { Box, Dialog, Grid, IconButton, Skeleton, Theme, Typography } from '@mui/material';
import { Form, Formik, FormikProps } from '../../../lib/formik/Formik';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { ReportsRightLayout } from '../../InvestorReport/index';
import { MFSubmitButton } from '../../../lib/formik';
import { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import dayjs from "../../../utils/dayjs";
import { DayJsDatePicker } from '../../onboarding/newDatePicker';
import { reportDownload } from '../../Reports/report-utils';
import { getHoldingReport, getReportsAnalystInvestorList, getReportsInvestorList } from '../../../redux-store/actions/reports';
import { RootStateType } from '../../../redux-store/reducers';
import { CONTENT_TYPE, MPOWER_ACCOUNT_TYPE_MASTER, REPORTS_DOWNLOAD_OPTION, USER_ROLES } from '../../../utils/constant';
import FormikSearchDropdown from '../../../lib/formik/FormikSearchDropdown';
import { ReportsListObj, ReportsListRes } from '../../../redux-store/types/api-types';
import { isModuleAllowed, maskInfo } from '../../../utils/utilityFunctions';
import { DialogCloseIcon, YellowWarning } from '../../../assets/SvgIcons';
import MFSelectField from '../../../lib/formik/SelectField';

interface Values {
    selectedInvestor: ReportsListObj | string | null;
    date: any;
    downloadOption: string;
}

export function HoldingReport({
    handleSideSectionDisabled
}: {
    handleSideSectionDisabled: (value: boolean) => void
}): JSX.Element {
    const { enqueueSnackbar } = useSnackbar();
    const currentDate = dayjs();
    const dispatch = useDispatch();
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const { isNavbarInfoLoading, businessDate } = useSelector((state: RootStateType) => state.navbarInfo);
    const [loading, setLoading] = useState<boolean>(false);
    const { iwappRmId = "", investorClientId = "", panNo: currentInvestorPanNo = "", investorName: currentInvestorName = "" } = useSelector((state: RootStateType) => state.id);
    const { role = '', subRole = '' } = useSelector((state: RootStateType) => state.auth);

    const formikRef = useRef<FormikProps<Values>>(null);

    const [investorListData, setInvestorListData] = useState<ReportsListRes>({} as ReportsListRes);
    const [isNoInvestorDialogVisible, setNoInvestorDialogVisible] = useState<boolean>(false)

    const isInvestor = isModuleAllowed({ value: role, compareArr: [USER_ROLES.INVESTOR] })

    const isPdfDownloadAllowed = true

    const todayDate = new Date();
    const formattedBusinessDate = businessDate?.length ? dayjs(businessDate, "DD/MM/YYYY").format("YYYY-MM-DD") : dayjs(todayDate).format("YYYY-MM-DD");

    const initialValues: Values = {
        selectedInvestor: isInvestor ? investorClientId : null,
        date: formattedBusinessDate,
        downloadOption: isPdfDownloadAllowed ? '' : "excel"
    }

    const holdingReportValidation = Yup.object().shape({
        selectedInvestor: isInvestor ?
            Yup
                .string()
                .nullable() :
            Yup
                .object()
                .nullable()
                .test(
                    "investor-required",
                    "Please select an investor",
                    function (value) { return !!value }
                ),
        date: Yup
            .date()
            .required('Date is required')
            .typeError('Date is required')

            // .min(dayjs("1950-01-01").format("YYYY-MM-DD"), "Date should not be less than 01/01/1950")
            .test('min-date', "Date should not be less than 01/01/1950", function (value) {
                if (!value) return true;
                return dayjs(value).isSameOrAfter(dayjs('1950-01-01'), 'day');
            })

            // .max(formattedBusinessDate, "Date should not be greater than system's date"),
            .test('max-today', "Date should not be greater than system's date", function (value) {
                if (!value) return true;
                return dayjs(value).isSameOrBefore(dayjs(formattedBusinessDate), 'day');
            }),
        downloadOption: Yup.string().required('Please select download option'),
    });


    const handleSubmit = async ({ selectedInvestor, date, downloadOption }: Values) => {
        try {
            setIsDownloading(true)
            handleSideSectionDisabled(true)
            const payload = {
                clientCode: isInvestor ? `${selectedInvestor ?? ""}` : `${(selectedInvestor as ReportsListObj)?.clientCode ?? ""}`,
                end: dayjs(date, "YYYY-MM-DD").format("DD/MM/YYYY"),
                ...(!isInvestor ? { rmId: `${iwappRmId}` } : {}),
                isPDF: (`${downloadOption ?? ""}`.toLowerCase()) === 'pdf',
                investorName: isInvestor ? (currentInvestorName ?? "") : ((selectedInvestor as ReportsListObj)?.customerName ?? ''),
                panNo: isInvestor ? (currentInvestorPanNo ?? "") : ((selectedInvestor as ReportsListObj)?.panNo ?? '')
            }
            const response = await (dispatch(getHoldingReport(payload)) as unknown as Promise<any>)
            if (response && [CONTENT_TYPE.PDF, CONTENT_TYPE.EXCEL].includes(response.type)) {
                enqueueSnackbar('File downloaded successfully.', {
                    variant: 'success',
                    autoHideDuration: 2000,
                });
                reportDownload(response as unknown as Blob, `HoldingReport.${response.type === CONTENT_TYPE.PDF ? 'pdf' : 'xlsx'}`);
            } else {
                enqueueSnackbar('File download failed!.', {
                    variant: 'error',
                    autoHideDuration: 2000,
                });
            }

        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setIsDownloading(false)
            handleSideSectionDisabled(false)
        }
    }

    const fetchInvestorList = async () => {
        try {
            setLoading(true);
            let response = {} as ReportsListRes

            if ([USER_ROLES.ANALYST].includes(role)) {
                response = await (dispatch(getReportsAnalystInvestorList({
                    analystId: `${iwappRmId}`
                })) as unknown as Promise<ReportsListRes>);
            } else {
                response = await (dispatch(getReportsInvestorList({
                    rmId: `${iwappRmId}`,
                    reportName: "holding-report"
                })) as unknown as Promise<ReportsListRes>);
            }

            setInvestorListData(response);
            if (!response?.isSuccess) {
                setNoInvestorDialogVisible(true)
            }
        } catch (e) {
            console.log("Error: ", e)
        } finally {
            setLoading(false);
        }
    };

    const handleNoInvestorDialogClose = () => {
        setNoInvestorDialogVisible(false)
    }

    const handleInvestorChange = (selectedObj: Record<string, any>) => {
        if (!formikRef.current) return

        const { values, setValues } = formikRef.current ?? {}

        if (!selectedObj) {
            setValues({
                ...values,
                selectedInvestor: null,
                date: formattedBusinessDate,
                downloadOption: isPdfDownloadAllowed ? '' : "excel"
            })

            return
        }

        setValues({
            ...values,
            selectedInvestor: selectedObj as ReportsListObj,
            date: formattedBusinessDate,
            downloadOption: isPdfDownloadAllowed ? '' : "excel"
        })
    }

    useEffect(() => {
        if (!isInvestor) {
            fetchInvestorList();
        }
    }, [])

    return (
        <>
            <ReportsRightLayout>
                <Typography
                    variant='h2'
                    sx={{
                        color: 'text.primary',
                        lineHeight: '25px',
                        letterSpacing: '0.01px',
                        mb: '40px'
                    }}
                >
                    Holding Report
                </Typography>
                <Formik
                    innerRef={formikRef}
                    validationSchema={holdingReportValidation}
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    enableReinitialize={true}
                >
                    {() => (
                        <Form>
                            <Grid container columnSpacing={3} rowSpacing={1.5} >
                                {!isInvestor && (
                                    <Grid item xs={12} lg={6}>
                                        {(loading || isNavbarInfoLoading) && <Skeleton
                                            animation={'wave'}
                                            height={90}

                                        />}
                                        {(!loading && !isNavbarInfoLoading) && (
                                            <FormikSearchDropdown
                                                name='selectedInvestor'
                                                label='Select Investor *'
                                                options={(investorListData?.result ?? [])?.map((obj: ReportsListObj) => ({
                                                    ...obj,
                                                    label: `${maskInfo(obj.customerName)} (${obj?.clientCode ?? ""}) ${obj?.investorType ? "(" + obj?.investorType + ")" : ""} ${`${obj?.isFamilyHead ?? ""}`.toLowerCase() === "y" ? "(Head Of Family)" : ""} ${MPOWER_ACCOUNT_TYPE_MASTER[obj?.accountType ?? ""] ? " - " + MPOWER_ACCOUNT_TYPE_MASTER[obj.accountType] : ""}`,
                                                    value: obj.clientCode
                                                }))}
                                                onChange={handleInvestorChange}
                                                disabled={
                                                    loading || isDownloading || isNavbarInfoLoading

                                                }
                                            />
                                        )}
                                    </Grid>
                                )}
                                <Grid item xs={12} lg={6}>
                                    {(loading || isNavbarInfoLoading) && <Skeleton
                                        animation={'wave'}
                                        height={90}

                                    />}
                                    {(!loading && !isNavbarInfoLoading) && <DayJsDatePicker
                                        id=""
                                        name='date'
                                        label="Select Date *"
                                        maxDate={new Date()}
                                        disabled={isDownloading || isNavbarInfoLoading}
                                        currentDate={formattedBusinessDate}
                                        resetDate
                                    />}
                                </Grid>
                                {isPdfDownloadAllowed && (
                                    <Grid item xs={12} lg={6}>
                                        {(loading || isNavbarInfoLoading) && <Skeleton
                                            animation={'wave'}
                                            height={90}

                                        />}
                                        {(!loading && !isNavbarInfoLoading) && <MFSelectField
                                            name='downloadOption'
                                            label="Select Download Option *"
                                            items={REPORTS_DOWNLOAD_OPTION}
                                            disabled={isDownloading}
                                        />}
                                    </Grid>
                                )}
                                <Grid item container columnSpacing={3} justifyContent={'flex-end'}>
                                    <Grid item xs={12} lg={6}>
                                        {(loading || isNavbarInfoLoading) && <Skeleton
                                            animation={'wave'}
                                            height={90}

                                        />}
                                        {(!loading && !isNavbarInfoLoading) && <MFSubmitButton
                                            label="Download"
                                            loadingEnable={isDownloading}
                                        />}
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Form >
                    )
                    }
                </Formik >
            </ReportsRightLayout >
            <Dialog
                onClose={handleNoInvestorDialogClose}
                open={isNoInvestorDialogVisible}
                maxWidth='sm'
                sx={(theme: Theme) => ({
                    '& .MuiPaper-root': {
                        borderRadius: '12px',
                        backgroundColor: theme.palette.common.white,
                        padding: "22px 24px",
                        width: "533px",
                    },
                })}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        rowGap: "8px",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center"
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                columnGap: "12px",
                                alignItems: "center"
                            }}
                        >
                            <YellowWarning />
                            <Typography
                                variant="h2"
                                sx={(theme: Theme) => ({ color: theme.palette.text.primary })}
                            >
                                No Investor Found!!
                            </Typography>
                        </Box>
                        <IconButton
                            aria-label="close"
                            onClick={handleNoInvestorDialogClose}
                            sx={{
                                padding: "5px"
                            }}
                        >
                            <DialogCloseIcon />
                        </IconButton>
                    </Box>
                </Box>
            </Dialog>
        </>
    );
};

